/**
 * UpgradeCard — el contenedor de checkout de la card de precios dentro de la app.
 *
 * Aquí vive TODO el estado y el cableado: los seats/periodo (sembrados desde la
 * URL), las queries de `me` y de precios, el auto-checkout post-registro y la
 * creación de la sesión de Stripe. La presentación es `<BusinessSubscriptionCard>`
 * (diseño de `temp_docs/CardBusiness.html`); la landing y /pricing sin sesión
 * usan su hermana `<BusinessPricingColumn>`.
 *
 * Aquí se decide además la VARIANTE de la card, que depende de cuántas fichas
 * activas tiene el usuario (`me.activeLocationIds`): con una, la card de un
 * local, sin −/+ y con el nombre de la ficha; con dos o más, el stepper
 * precargado a ese número, que es también su tope.
 *
 * Antes este fichero tenía 4 variantes visuales (`family × trialUsed`) más sus
 * sub-componentes. El rediseño las colapsó: lo único que cambia hoy es la
 * etiqueta del CTA y la letra pequeña, que dependen de `trialAvailable`. El
 * badge de "precio de lanzamiento" ya no se gatea por familia — se anuncia sin
 * condición, igual que ya se hacía en la landing y en /pricing.
 *
 * Se conserva `AlreadyProCard`: no es una card de precios sino un callejón sin
 * salida para quien ya paga, y se devuelve antes de llegar a la card.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Crown, ArrowRight, AlertTriangle } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import {
  createCheckoutSession, createPortalSession, getActivePricing,
} from '@/api/billingService';
import { getGbusinessMe } from '@/api/gbusinessService';
import { useAuth } from '@/lib/AuthContext';
import { gbusinessErrorMessage } from '@/api/gbusinessErrors';
import BusinessSubscriptionCard from '@/components/billing/business/BusinessSubscriptionCard';
import CtaLink from '@/components/CtaLink';
import { useGbusinessLocations } from '@/hooks/useGbusinessLocations';
import { withCta } from '@/lib/ctaTracking';
import { PRO_MAX_SEATS } from '@/lib/priceBreakdown';
import { contactSales } from '@/lib/salesContact';

/**
 * Periodo con el que abre la card, leído de la URL.
 *
 * MENSUAL por defecto, igual que el orden del toggle (mensual a la izquierda).
 * Solo un `?period=annual` explícito sube a anual, para que al volver de
 * login/registro se respete lo que el usuario había elegido antes de irse.
 *
 * Está factorizado porque lo necesitan DOS sitios —el estado inicial y el
 * `periodArg` del auto-checkout— y si divergen la card enseña un periodo y
 * Stripe cobra el otro en el flujo post-registro. Con una sola expresión no
 * pueden desincronizarse.
 */
function periodFromParams(searchParams) {
  return searchParams.get('period') === 'annual' ? 'annual' : 'monthly';
}

/* ─── Already-Pro card ────────────────────────────────────────────────────── */

// Se muestra cuando un usuario YA Pro abre la página de Pricing: en vez de
// dejarle pagar otra vez, le confirmamos que su cuenta ya es Pro y le mandamos
// a /settings a gestionar la suscripción (cambiar plan, facturas, cancelar).
function AlreadyProCard() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#feae2c]/15">
        <Crown className="h-7 w-7 text-[#feae2c]" />
      </div>

      <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3">
        {t('billing.card.alreadyPro.badge')}
      </span>

      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
        {t('billing.card.alreadyPro.title')}
      </h2>
      <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
        {t('billing.card.alreadyPro.description')}
      </p>

      <button
        type="button"
        onClick={() => navigate('/settings')}
        className="w-full bg-[#feae2c] hover:bg-[#f09f1a] text-black font-semibold rounded-lg py-3 text-sm transition-colors flex items-center justify-center gap-2"
      >
        {t('billing.card.alreadyPro.cta')}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ─── Payment-issue card ──────────────────────────────────────────────────── */

// Simétrica a `AlreadyProCard`, para el otro extremo: la suscripción existe y
// sigue viva en Stripe, pero hay un cobro sin resolver.
//
// Sin esta rama, un `past_due` al que se le agotó la gracia dejaba de ser Pro y
// caía en la card de PRECIOS — en las tres superficies donde se monta esta
// tarjeta (el overlay de las páginas Pro, Ajustes y /Pricing). Es decir, le
// ofrecíamos contratar de nuevo mientras Stripe seguía reintentando cobrarle la
// suscripción que ya tenía: dos cargos por lo mismo. Lo que necesita es
// arreglar la tarjeta, así que le mandamos al portal.
function PaymentIssueCard() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  async function openPortal() {
    setLoading(true);
    try {
      const url = await createPortalSession({ returnPath: window.location.pathname });
      window.location.href = url;
    } catch (e) {
      toast.error(gbusinessErrorMessage(e, t));
      setLoading(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-7 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
        <AlertTriangle className="h-7 w-7 text-red-600" />
      </div>

      <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-2">
        {t('billing.card.paymentIssue.title', {
          defaultValue: 'No hemos podido cobrar tu suscripción',
        })}
      </h2>
      <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
        {t('billing.card.paymentIssue.description', {
          defaultValue: 'Tu suscripción sigue activa en nuestro sistema. Actualiza tu método de pago para recuperar el acceso — no hace falta contratar de nuevo.',
        })}
      </p>

      <button
        type="button"
        onClick={openPortal}
        disabled={loading}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg py-3 text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading
          ? t('loading')
          : t('billing.card.paymentIssue.cta', { defaultValue: 'Actualizar método de pago' })}
        {!loading && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
}

/** Estados de Stripe en los que hay un cobro sin resolver sobre una suscripción
 *  que TODAVÍA existe. Espejo de la guarda de `create_checkout_session`: si la
 *  interfaz ofreciera checkout aquí, el backend lo rechazaría con
 *  `subscription_payment_pending`. Una baja de verdad no entra — el webhook
 *  `subscription.deleted` deja el status en `canceled`. */
const PAYMENT_ISSUE_STATUSES = ['past_due', 'unpaid', 'incomplete'];

/**
 * ¿Hay un cobro sin resolver sobre una suscripción que todavía existe?
 *
 * Exportado porque `ProGatePanel` necesita saberlo ANTES de decidir su layout:
 * a quien tiene un recibo devuelto no se le vende la función, se le pide la
 * tarjeta, así que el muro de dos columnas se repliega a la card sola. Copiar
 * allí la lista de estados la habría dejado divergir en el primer cambio.
 *
 * @param {any} me Respuesta de `getGbusinessMe()`.
 */
export function hasPaymentIssue(me) {
  return PAYMENT_ISSUE_STATUSES.includes(me?.subscriptionStatus) && !me?.adminAssignedPro;
}

/* ─── UpgradeCard ─────────────────────────────────────────────────────────── */

/**
 * @param {{ seatForLocationId?: string, initialSeats?: number, ctaId?: string,
 *           origenCtaId?: string, onClose?: () => void, nota?: string }} props
 *
 * `ctaId` lo pone QUIEN monta la card, porque esta card se monta en cinco
 * sitios distintos (Ajustes, /pricing, el modal de precios, la vista previa
 * bloqueada y el de cambiar de plan) y el mismo botón significa una cosa
 * distinta en cada uno. Con un id fijo se mezclarían cinco embudos.
 *
 * `origenCtaId` es el CTA que abrió el modal donde vive esta card, cuando lo
 * hubo: el muro de Automatizaciones, el de Métricas, el de Publicaciones. Se
 * lleva el ALTA DE PLAN; el `ctaId` de arriba se queda el click. Son dos
 * preguntas distintas —qué función bloqueada empuja a pagar, y qué pantalla de
 * precios se pulsa— y una sola respuesta no vale para las dos (§124).
 *
 * `seatForLocationId` es el local que motivó la compra, cuando la tarjeta se
 * abre desde el interruptor de Business de un local concreto. Viaja hasta la
 * metadata de la suscripción de Stripe y vuelve en el webhook de creación: sin
 * él, el asiento recién comprado cae en el primer local por orden de llegada de
 * Google, y el cliente acaba de pagar por otro.
 *
 * `onClose` pinta la X de la card: solo dentro de `PricingModal`. Montada en
 * una página (Ajustes, /pricing, los muros Pro) no hay nada que cerrar.
 *
 * `nota` es una línea encima del título que dice por qué se ha abierto
 * («ya has usado tu publicación gratuita»).
 */
export default function UpgradeCard({
  seatForLocationId, initialSeats, ctaId, origenCtaId, onClose, nota = '',
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  // Inicializa seats/period desde la URL para que, al volver autenticado a
  // /Pricing?autocheckout=1&seats=X&period=Y, la card refleje la selección
  // que el usuario hizo antes de registrarse/loguearse.
  //
  // `initialSeats` es la otra forma de sembrarlo, para quien abre esta card ya
  // sabiendo CUÁNTOS locales quiere cubrir — hoy, la tarjeta de «dale Business a
  // todos» del alta. Sin ella la card arrancaba en 1 y el checkout cobraba un
  // asiento a quien acababa de pulsar un botón que decía «en los 5»: la promesa
  // se perdía justo en el paso que cobra.
  //
  // La URL manda por encima a propósito: ahí viene la vuelta del registro, y en
  // ese punto el usuario YA eligió un número a mano.
  //
  // Si no llega ninguna de las dos, `null`: el número sale de las fichas activas,
  // que todavía no han llegado cuando se monta la card (ver `seats` más abajo).
  const [seatsChoice, setSeats] = useState(() => {
    const s = parseInt(searchParams.get('seats'), 10);
    if (Number.isInteger(s) && s >= 1) return s;
    if (Number.isInteger(initialSeats) && initialSeats >= 1) return initialSeats;
    return null;
  });
  const [billing, setBilling] = useState(() => periodFromParams(searchParams));
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['gbusiness', 'me'],
    queryFn: getGbusinessMe,
    enabled: !!isAuthenticated,
  });

  // Un usuario ya Pro no debe poder volver a pagar desde Pricing: ni con el CTA
  // ni con el auto-checkout. `meLoading` solo es relevante autenticado (query
  // deshabilitada para anónimos).
  //
  // Un comp en MODO CLOSING es Pro pero SÍ contrata aquí: no tiene suscripción de
  // Stripe que duplicar, y el backend le deja pasar la misma guarda.
  const isPro = !!me?.isPro && !me?.closingMode;
  const meReady = !isAuthenticated || !meLoading;
  // Suscripción viva con un cobro pendiente. Bloquea el checkout igual que
  // `isPro` — si no, se le crearía una segunda suscripción sobre el mismo
  // customer mientras Stripe reintenta cobrar la primera.
  const paymentIssue = hasPaymentIssue(me);

  const { data: activePricing, isLoading: pricingLoading } = useQuery({
    queryKey: ['billing', 'active-pricing'],
    queryFn: getActivePricing,
    staleTime: Infinity,
  });

  // ── Variante y número de locales ──────────────────────────────────────────
  //
  // `connected` son las fichas ACTIVAS, la misma cuenta que usa «Business para
  // todos», y es el número con el que arranca el stepper. El tope, en cambio,
  // son TODAS las fichas de sus cuentas de Google, activas o no: una ficha sin
  // verificar está oculta, pero el cliente la va a verificar y quiere pagarla
  // ya. Si la URL o quien monta la card piden más, manda lo que el usuario ya
  // eligió. Y nunca por encima de lo que cubre Business (30).
  //
  // `seatForLocationId` sin `initialSeats` es una compra para UNA ficha concreta
  // (el interruptor de Business de un local): ahí va la card de un local, con su
  // nombre, aunque el usuario tenga más.
  //
  // Las ubicaciones salen de la misma caché que el resto de la app. Mientras
  // llegan, el tope es el de las fichas activas.
  const { allLocations } = useGbusinessLocations({ enabled: !!isAuthenticated && !!me?.connected });
  const activeIds = me?.activeLocationIds || [];
  const connected = activeIds.length;
  const totalLocations = allLocations.length;
  const pricingMax = activePricing?.maxSeats || PRO_MAX_SEATS;
  const forOneLocation = !!seatForLocationId && !(Number.isInteger(initialSeats) && initialSeats >= 1);
  const wanted = Math.max(connected, totalLocations, seatsChoice || 0);
  const variant = !forOneLocation && wanted >= 2 ? 'multi' : 'single';
  const maxSeats = Math.max(1, Math.min(pricingMax, wanted));
  const seats = variant === 'single'
    ? 1
    : Math.max(1, Math.min(maxSeats, seatsChoice ?? connected));

  // El nombre de la ficha para «Para: …».
  const singleLocationId = variant === 'single' ? (seatForLocationId || activeIds[0]) : null;
  const locationName = singleLocationId
    ? (allLocations.find((l) => l.locationId === singleLocationId)?.title || '')
    : '';

  // Auto-checkout: al aterrizar autenticado en /Pricing?autocheckout=1 (tras
  // registro o login), lanzamos Stripe con los seats/period de la URL una sola
  // vez. El ref evita doble disparo por re-render / StrictMode. Debe ir ANTES
  // del early-return del spinner para no romper las reglas de hooks; no
  // depende de activePricing porque el checkout solo necesita seats/period.
  const autoCheckoutFired = useRef(false);
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    if (!isAuthenticated) return;
    if (searchParams.get('autocheckout') !== '1') return;
    // Espera a saber si ya es Pro antes de decidir; si lo es, no disparamos
    // checkout (se renderiza AlreadyProCard en su lugar).
    if (!meReady) return;
    if (isPro) return;
    // Un impago pendiente tampoco dispara: sería el mismo doble cobro, pero
    // automático y sin que el usuario llegue a tocar un botón.
    if (paymentIssue) return;
    autoCheckoutFired.current = true;
    const s = parseInt(searchParams.get('seats'), 10);
    const seatsArg = Number.isInteger(s) && s >= 1 ? s : seats;
    const periodArg = periodFromParams(searchParams);
    // Tras pagar, el usuario recién registrado aterriza en Dispositivos
    // (/Devices?stripe=success) en lugar de volver a /Pricing.
    //
    // Sin `ctaId`, y a propósito: aquí no ha pulsado nada: viene de registrarse
    // y esta card solo está de paso. Mandando el suyo (`precios.sesion.empezar-trial`)
    // le robaría el alta al botón de la landing que empezó todo, que es quien
    // la merece. Vacío, el backend cae a `signup_cta_id` y se la lleva él (§124).
    startCheckout(seatsArg, periodArg, '/Devices', null);
  }, [isAuthenticated, searchParams, meReady, isPro, paymentIssue]);

  // Espera a pricing y, si está autenticado, también a `me` — así un usuario
  // Pro nunca ve por un instante la card de checkout antes de AlreadyProCard.
  if (pricingLoading || !activePricing || !meReady) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 text-[#feae2c] animate-spin" />
      </div>
    );
  }

  // Usuario ya Pro → no se le deja pagar de nuevo; le ofrecemos ir a /settings.
  if (isPro) {
    return <AlreadyProCard />;
  }

  // Impago pendiente → tampoco se le deja contratar otra vez; lo que necesita
  // es arreglar el método de pago. Va DESPUÉS de `isPro` a propósito: dentro de
  // la ventana de gracia sigue siendo Pro y el aviso se lo da el banner, no
  // esta card.
  if (paymentIssue) {
    return <PaymentIssueCard />;
  }

  // Esta card SIEMPRE cotiza la familia ACTIVA, nunca la que el usuario tenga
  // guardada, porque es la card de CHECKOUT y `create_checkout_session` usa
  // `_active_price_family()` y además sobrescribe `users.price_family` con
  // ella. Un usuario no-Pro puede arrastrar un `priceFamily` viejo (checkout
  // abandonado, o baja: el campo no se limpia), y cotizarle esa familia sería
  // enseñarle un precio y cobrarle otro.
  //
  // Ojo con la asimetría: `ResizePlanModal` hace lo CONTRARIO y cotiza la
  // familia del usuario, porque `resize_subscription` sí usa los Price IDs de
  // su familia (grandfathering, §26.15). Cada superficie tiene que reflejar lo
  // que su endpoint va a cobrar de verdad.
  //
  // trialUsed defaults false (anon visitors are new customers).
  // `trialAvailable` además exige que STRIPE_TRIAL_DAYS > 0 — si el backend
  // está configurado sin trial (testing o lanzamiento sin promo), el CTA pasa a
  // "contratar ahora" y la letra pequeña de la prueba no aparece.
  const trialUsed = !!me?.trialUsed;
  const trialAvailable = !trialUsed && (activePricing.trialDays || 0) > 0;

  // Crea la sesión de Stripe y navega (top-level) al checkout. Reusada por el
  // CTA (usuario ya autenticado) y por el auto-checkout post-registro/login.
  // `returnPath` por defecto es la página actual (Pricing, Settings free card,
  // PreviewOverlay sobre Reviews/Automations/etc.) en vez de rebotar siempre a
  // /Settings (decisiones §26.20); el auto-checkout lo sobreescribe a /Devices
  // para que el usuario recién registrado aterrice en Dispositivos tras pagar.
  async function startCheckout(seatsArg, periodArg, returnPath = window.location.pathname,
                               ctaIdArg = origenCtaId || ctaId) {
    setCheckoutLoading(true);
    try {
      const url = await createCheckoutSession({
        seats: seatsArg,
        period: periodArg,
        seatForLocationId,
        returnPath,
        // El id viaja a la metadata de Stripe para que el webhook pueda
        // atribuirle el alta de plan (§124). El click ya se midió al pulsar;
        // esto es la otra mitad del embudo, la que dice si convirtió.
        ctaId: ctaIdArg,
      });
      window.location.href = url;
    } catch (e) {
      toast.error(gbusinessErrorMessage(e, t));
      setCheckoutLoading(false);
    }
    // Sin `finally`: en el camino feliz la pestaña navega a Stripe y el
    // componente se desmonta; solo reseteamos el loading en el catch.
  }

  function handleCheckout() {
    if (!isAuthenticated) {
      // El usuario anónimo va a registrarse/loguearse; codificamos la
      // selección en el `redirect` que Login/Register ya entienden, de modo
      // que al volver a /Pricing?autocheckout=1 disparemos Stripe sin más toques.
      const target = `/pricing?autocheckout=1&seats=${seats}&period=${billing}`;
      navigate(withCta(`/register?redirect=${encodeURIComponent(target)}`));
      return;
    }
    // Desde la página de Pricing (usuario ya logueado), tras pagar aterriza en
    // Dispositivos igual que el flujo de registro. En otros contextos donde se
    // monta esta card (Settings free card, PreviewOverlay sobre Reviews/
    // Automations) `returnPath` por defecto = página actual, para no sacar al
    // usuario de donde estaba.
    const onPricing = /^\/pricing$/i.test(window.location.pathname);
    startCheckout(seats, billing, onPricing ? '/Devices' : undefined);
  }

  // Más fichas de las que cubre Business: el resto es Enterprise.
  const enterpriseLink = connected > pricingMax ? (
    <p className="m-0 text-[13px] text-stone-600">
      {t('billing.sub.enterpriseMore', { max: pricingMax })}{' '}
      <CtaLink
        as="button"
        type="button"
        cta="app.precios.enterprise-mas-30"
        onClick={contactSales}
        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-amber-800 underline hover:text-amber-900"
      >
        {t('billing.sub.enterpriseLink')}
      </CtaLink>
    </p>
  ) : null;

  return (
    <BusinessSubscriptionCard
      // `activePricing.totals` ya son los de la familia activa — la misma que
      // usará el checkout. Ver el comentario de arriba: aquí NO se usa
      // `totalsForFamily(…, me.priceFamily)`, a diferencia de ResizePlanModal.
      pricing={activePricing}
      variant={variant}
      seats={seats}
      maxSeats={maxSeats}
      connectedCount={connected}
      locationName={locationName}
      billing={billing}
      onSeatsChange={setSeats}
      onBillingChange={setBilling}
      onCheckout={handleCheckout}
      ctaId={ctaId}
      checkoutLoading={checkoutLoading}
      trialDays={activePricing.trialDays}
      trialAvailable={trialAvailable}
      onClose={onClose}
      nota={nota}
      enterpriseLink={enterpriseLink}
    />
  );
}
